/**
 * Web Speech API Hooks fuer Scene 2 — STT (Listen) + TTS (Speak).
 *
 * Browser-native, kein Backend / kein API-Key. In Production wuerde das auf
 * AWS Transcribe + Polly umgestellt — aber fuer den Demo-Call reicht das
 * Browser-Native vollkommen.
 *
 * Browser-Support:
 *   * STT: Chrome/Edge/Safari (webkitSpeechRecognition prefix)
 *   * TTS: alle modernen Browser (SpeechSynthesisUtterance)
 */
import { useCallback, useEffect, useRef, useState } from "react";

// Web Speech API ist nicht in den Standard-TS-Types — minimale Typdef.
interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly [index: number]: { readonly transcript: string };
}
interface SpeechRecognitionResultList {
  readonly length: number;
  readonly [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionEvent extends Event {
  readonly results: SpeechRecognitionResultList;
}
interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onresult: ((e: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: Event) => void) | null;
}
interface SpeechRecognitionCtor {
  new (): SpeechRecognitionInstance;
}
declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  }
}

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

export interface UseSpeechRecognitionResult {
  /** True wenn der Browser STT unterstuetzt. */
  isSupported: boolean;
  /** True waehrend aktiv gehoert wird. */
  isListening: boolean;
  /** Live-Transkription (interim + final). */
  transcript: string;
  /** Start hoeren. */
  start: () => void;
  /** Stop hoeren — triggert `onFinal` mit der finalen Transkription. */
  stop: () => void;
}

export function useSpeechRecognition(
  onFinal: (transcript: string) => void,
  lang = "en-US",
): UseSpeechRecognitionResult {
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const finalTranscriptRef = useRef("");

  const Ctor = getRecognitionCtor();
  const isSupported = Ctor !== null;

  useEffect(() => {
    if (!Ctor) return;
    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = lang;
    rec.onresult = (e: SpeechRecognitionEvent) => {
      let interim = "";
      let final = finalTranscriptRef.current;
      for (let i = 0; i < e.results.length; i++) {
        const result = e.results[i];
        if (result.isFinal) {
          final += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }
      finalTranscriptRef.current = final;
      setTranscript((final + interim).trim());
    };
    rec.onend = () => {
      setIsListening(false);
    };
    rec.onerror = () => {
      setIsListening(false);
    };
    recognitionRef.current = rec;
    return () => {
      try { rec.stop(); } catch { /* ignore */ }
    };
  }, [Ctor, lang]);

  const start = useCallback(() => {
    if (!recognitionRef.current) return;
    finalTranscriptRef.current = "";
    setTranscript("");
    try {
      recognitionRef.current.start();
      setIsListening(true);
    } catch {
      // Already running — ignore.
    }
  }, []);

  const stop = useCallback(() => {
    if (!recognitionRef.current) return;
    try {
      recognitionRef.current.stop();
    } catch { /* ignore */ }
    setIsListening(false);
    const finalText = finalTranscriptRef.current.trim();
    if (finalText) onFinal(finalText);
  }, [onFinal]);

  return { isSupported, isListening, transcript, start, stop };
}

/**
 * TTS via SpeechSynthesisUtterance. Gibt einen `speak(text)`-Callback
 * zurueck der die `isSpeaking`-Flag setzt fuers UI.
 */
export function useSpeechSynthesis(lang = "en-US"): {
  speak: (text: string) => void;
  cancel: () => void;
  isSpeaking: boolean;
} {
  const [isSpeaking, setIsSpeaking] = useState(false);

  const speak = useCallback((text: string) => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = 1.05;
    utterance.pitch = 1.0;
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
  }, [lang]);

  const cancel = useCallback(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  }, []);

  return { speak, cancel, isSpeaking };
}
